import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTE_PATHS } from "../../../../../common/enums/routing-enums";
import LeafTile from "../../../../../common/leaf/LeafTile/LeafTile";
import { ErrorTile } from "../../../../../common/components/ErrorTile/ErrorTile";
import { IconNoCustomerOrders } from "../../../../../common/icons/IconNoCustomerOrders/IconNoCustomerOrders";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { useCustomerOrdersData } from "../useCustomerOrdersData";
import { CustomerOrdersCardMobile } from "../CustomerOrdersCardMobile/CustomerOrdersCardMobile";
import { CustomerOrdersCardDesktop } from "../CustomerOrdersCardDesktop/CustomerOrdersCardDesktop";
import { MAX_ORDERS } from "./CustomerOrdersWrapper.utils";
import defaultContent from "../../../../../content/groupManagement";
import TEALIUM from "../../../../../common/enums/tealium-enums";
import { track } from "../../../../../common/util/tealium";
import "./CustomerOrdersWrapper.scss";

type CustomerOrdersCardProps = {
  isCustomerListEnabled: boolean;
  isOverviewLoading?: boolean;
};

const CustomerOrdersLoadingSkeleton = () => (
  <>
    <div className="customer-orders-card__loading-mobile">
      <LeafRectangleLoader isLoading height="5rem" width="100%" margin="0 0 0.5rem 0" numRows={3} />
    </div>
    <div className="customer-orders-card__loading-desktop">
      <LeafRectangleLoader isLoading height="5.5rem" width="100%" margin="0" numRows={1} numColumns={3} />
    </div>
  </>
);

export const CustomerOrdersCard = ({
  isOverviewLoading = false,
  isCustomerListEnabled: _isCustomerListEnabled,
}: CustomerOrdersCardProps) => {
  const { t } = useTranslation(["groupManagement"]);
  const { orders, isLoading, isError, refetch } = useCustomerOrdersData(isOverviewLoading);
  const displayedOrders = orders.slice(0, MAX_ORDERS);

  const showLoading = isOverviewLoading || isLoading;

  const handleViewAllClick = () => {
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      TEALIUM.CATEGORY.GROUP_MANAGEMENT,
      "global_cml",
      "",
      "customer_orders_view_all",
    );
  };

  if (isError && !isOverviewLoading) {
    return (
      <LeafTile hideBorder={true}>
        <ErrorTile
          clickFunction={refetch}
          errorStatus={isError}
          isLoading={isLoading}
          tealiumTrackId="GroupManagement_CustomerOrders"
        />
      </LeafTile>
    );
  }

  return (
    <div className="customer-orders-card">
      <div className="customer-orders-card__header">
        <span className="customer-orders-card__title" data-testid="customer-orders-header">
          {t("customerOrders", defaultContent["customerOrders"])}
        </span>
        {!showLoading && (
          <Link
            to={ROUTE_PATHS.CUSTOMER_ORDER}
            className="customer-orders-card__view-all"
            data-testid="customer-orders-view-all"
            onClick={handleViewAllClick}
          >
            {t("viewAll", defaultContent["viewAll"])}
          </Link>
        )}
      </div>
      {showLoading ? (
        <CustomerOrdersLoadingSkeleton />
      ) : orders.length === 0 ? (
        <div className="customer-orders-card__empty-state">
          <div className="customer-orders-card__empty-illustration">
            <IconNoCustomerOrders />
          </div>
          <p className="customer-orders-card__empty-message">
            {t("noCustomerOrders", defaultContent["noCustomerOrders"])}
          </p>
        </div>
      ) : (
        <>
          <div className="customer-orders-card__mobile">
            <CustomerOrdersCardMobile orders={displayedOrders} />
          </div>
          <div className="customer-orders-card__desktop">
            <CustomerOrdersCardDesktop orders={displayedOrders} />
          </div>
        </>
      )}
    </div>
  );
};
